import { AllowedGroundingFiles, ChatApi, ChatExchangeModel, ChatTurn } from "../../../@api";

import { ChatThreadExtendedModel } from "../../models/interfaces";
import { IStoreContext } from "../../stores/StoreContext";
import { ChatErrorResponseHandlingMiddleware } from "../ApiMiddlewares";
import { ChatApiService } from "../ApiServices/ChatApiService";
import GenericService from "../GenericService";
import { ThreadService } from "./ThreadService";

export class ChatService extends GenericService<ChatApi, ChatApiService> {
  private readonly threadService: ThreadService;

  constructor(storeContext: IStoreContext) {
    super(storeContext, ChatApi, ChatApiService);
    this.threadService = new ThreadService(storeContext);
  }

  protected override get afterGlobalMiddlewares() {
    return [new ChatErrorResponseHandlingMiddleware(this.storeContext.userStore)];
  }

  public async sendChatMessage(
    requestThread: ChatThreadExtendedModel | undefined,
    message: string,
    files?: File[],
  ): Promise<ChatExchangeModel | undefined> {
    const { threadStore, userStore, chatStore, settingsStore, globalErrorsStore } = this.storeContext;

    threadStore.setPendingPromptInThread(requestThread?.id, message);
    threadStore.threadErrors = [];

    const history =
      requestThread?.exchanges?.slice().map(exchange => this.createChatTurn(exchange.prompt, exchange.answer ?? "")) ??
      [];

    history.push({ fromUser: message.trim() } as ChatTurn);

    const safeHistory = JSON.stringify(history.filter(h => h?.fromUser?.trim()?.length));

    let pendingThread: ChatThreadExtendedModel = {} as ChatThreadExtendedModel;
    pendingThread = { ...requestThread, pendingPrompt: message } as ChatThreadExtendedModel;

    try {
      const commonParams = {
        threadId: requestThread?.id,
        chatbotSettingsOverrides: {},
        responseLanguage: userStore.currentLanguage.code,
        enableWebSearch: chatStore.isWebSearchActive,
      };

      const response = files?.length
        ? await this.apiService.onPostChatWithFile({
            history: [safeHistory] as any,
            ...commonParams,
            files: files,
          })
        : await this.apiService.onPostChat({
            simpleChatRequestDto: {
              history,
              ...commonParams,
            },
          });

      pendingThread = {
        ...pendingThread,
        ...response,
      };
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : "Unexpected error";

      globalErrorsStore.addErrorIfNotExists(errorMessage);
      console.error("unexpected error", error);
      return;
    } finally {
      pendingThread = { ...pendingThread, pendingPrompt: undefined } as ChatThreadExtendedModel;
      const activeThread = threadStore.activeThread;

      // NOTE #63781 if a user is on NewChat (activeThread?.id is undefined), set the activeThread to the pendingThread after the response (with the created/new threadId) is received
      if (!activeThread?.id && activeThread?.pendingPrompt) {
        threadStore.setActiveThread(pendingThread);
      }

      threadStore.updateOrCreatePendingThread(pendingThread);

      if (settingsStore.persistChat) {
        void this.threadService.loadThreads();
      }
    }
  }

  private createChatTurn(fromUser: string, fromBot: string): ChatTurn {
    return {
      fromUser,
      fromBot,
      combinedCharacterCount: fromUser.length + fromBot.length,
    };
  }

  public async getAllowedGroundingFiles(): Promise<AllowedGroundingFiles> {
    return this.apiService.chatGetAllowedGroundingFiles();
  }
}
